import {Injectable} from '@angular/core';
import {UuidService} from "../uuid.service";
declare var require: any;
const ThumbmarkJS = require('@thumbmarkjs/thumbmarkjs');
import {StorageService} from "../storage.service";
import {HashService} from "../hash.service";
import {GpuFingerprintService} from "./gpuFingerprint.service";

@Injectable({
    providedIn: 'root'
})
export class FingerprintService {
  private readonly DB_KEY = 'sn_fingerprint_device_uuid';

  constructor(
    private uuidService: UuidService,
    private storageService: StorageService,
    private hashService: HashService,
    private gpuFingerprintService: GpuFingerprintService
  ) {}

  async getFullIdentity() {
    try {
      const fpData = await ThumbmarkJS.getThumbmark();

      let deviceUuid = await this.storageService.read(this.DB_KEY);
      if (!deviceUuid) {
        deviceUuid = this.uuidService.generateUUID();

        await this.storageService.save(this.DB_KEY, deviceUuid);

        localStorage.setItem(this.DB_KEY, deviceUuid);
      }

      const comps = fpData && fpData.components ? fpData.components : {};

      let audioHash  = comps.audio  ? this.hashService.getParametersHash(comps.audio) : null;
      let mathHash   = comps.math   ? this.hashService.getParametersHash(comps.math)  : null;
      let webglHash  = comps.webgl  ? this.hashService.getParametersHash(comps.webgl) : null;
      let canvasHash = comps.canvas ? this.hashService.getParametersHash(comps.canvas): null;
      let screenHash = comps.screen ? this.hashService.getParametersHash(comps.screen): null;

      let timezone = (comps.locales && comps.locales.timezone) ? comps.locales.timezone : null;

      return {
        device_uuid: deviceUuid,
        hash: fpData ? this.hashService.getParametersHash(fpData.thumbmark) : null,
        math: mathHash,
        math2: this.hashService.getParametersHash(this.getHardwareEnvironment()),
        webgl: webglHash,
        gpu_math: this.hashService.getParametersHash(await this.gpuFingerprintService.identity()),
        canvas: canvasHash,
        audio: audioHash,
        screen: screenHash,
        timezone: timezone,
        local: this.hashService.getParametersHash(this.getLocaleSignals()),
        isStandalone: window.matchMedia('(display-mode: standalone)').matches
      };
    } catch (error) {
      return localStorage.getItem(this.DB_KEY) || this.uuidService.generateUUID();
    }
  }

  getMathFingerprint () {
    const results = {
      machineEps: undefined,
      largeFloat32: undefined,
      p32: undefined,
      poly1: undefined,
      poly2: undefined,
      poly3: undefined,
      poly4: undefined,
      bitShift1: undefined,
      bitShift2: undefined,
      bitShift3: undefined,
      bigSin: undefined,
      bigCos: undefined,
      tanAtan: undefined
    };

    let eps = 1.0;
    while ((1.0 + (eps / 2.0)) !== 1.0) {
      eps /= 2.0;
    }
    results.machineEps = eps;

    results.largeFloat32 = new Float32Array([123456.7890123456789])[0];
    results.p32 = Math.fround(Math.PI);

    results.poly1 = Math.pow(Math.E, Math.LN2);
    results.poly2 = Math.sinh(1) + Math.cosh(1);
    results.poly3 = Math.expm1(1) - (Math.E - 1);
    results.poly4 = Math.hypot(1e300, 1e300);

    results.bitShift1 = (0xffffffff >> 1);
    results.bitShift2 = (-1 >>> 1);
    results.bitShift3 = Math.clz32(1.5);

    results.bigSin = Math.sin(1e16);
    results.bigCos = Math.cos(1e16);
    results.tanAtan = Math.tan(Math.atan(1e15));

    const rawString = JSON.stringify(results);
    let hash = 0x811c9dc5;
    for (let i = 0; i < rawString.length; i++) {
      hash ^= rawString.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }

    return (hash >>> 0).toString(16);
  };

  getHardwareEnvironment(){
    return {
      math: this.getMathFingerprint(),
      cores: navigator.hardwareConcurrency || 0,
      isTouch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      hasOrientation: typeof window.orientation !== 'undefined' || 'orientation' in screen
    };
  };

  getLocaleSignals() {
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      offset: new Date().getTimezoneOffset(),
      languages: navigator.languages || [navigator.language],
      localeString: new Date(1700000000000).toLocaleString(),
      calendar: Intl.DateTimeFormat().resolvedOptions().calendar,
    };
  };

}
